import { Close, Star, StarBorder } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { useCallback, useMemo, useRef, useEffect } from 'react'
import { useTheme } from '../theme'

interface Props {
  isFavourite: boolean
  onToggle: () => void
  size?: 'small' | 'medium' | 'large'
  variant?: 'star' | 'remove'
}

const FavouriteButton = ({
  isFavourite,
  onToggle,
  size = 'medium',
  variant = 'star',
}: Props) => {
  const theme = useTheme()
  const onToggleRef = useRef(onToggle)

  useEffect(() => {
    onToggleRef.current = onToggle
  }, [onToggle])

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onToggleRef.current()
  }, [])

  const ariaLabel = useMemo(() => {
    if (variant === 'remove') {
      return 'Remove from favourites'
    }
    return isFavourite ? 'Remove from favourites' : 'Add to favourites'
  }, [variant, isFavourite])

  const icon = useMemo(() => {
    if (variant === 'remove') {
      return <Close fontSize={size} />
    }
    // Default star behavior
    return isFavourite ? (
      <Star fontSize={size} />
    ) : (
      <StarBorder fontSize={size} />
    )
  }, [variant, isFavourite, size])

  const iconColor = useMemo(() => {
    if (variant === 'remove') {
      return theme.palette.grey[400]
    }
    return isFavourite ? theme.palette.warning.main : theme.palette.grey[400]
  }, [variant, isFavourite, theme.palette])

  const hoverColor = useMemo(() => {
    if (variant === 'remove') {
      return theme.palette.common.white
    }
    return isFavourite
      ? theme.palette.warning.dark
      : theme.palette.warning.light
  }, [variant, isFavourite, theme.palette])

  return (
    <IconButton
      onClick={handleClick}
      size={size}
      aria-label={ariaLabel}
      aria-pressed={variant === 'star' ? isFavourite : undefined}
      sx={{
        color: iconColor,
        '&:hover': {
          color: hoverColor,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      {icon}
    </IconButton>
  )
}

export default FavouriteButton
